import { useMemo } from 'react';
import type { CreditRatesResponse, StorageInfo } from '../../../../features/organizations/types';
import {
  DEFAULT_GENERATION_MODEL,
  GENERATION_MODEL_SPEED,
  GENERATION_MODEL_QUALITY,
  GENERATION_MODEL_LITE,
  RESOURCE_TYPE_IMAGE_SPEED,
  RESOURCE_TYPE_IMAGE_SPEED_2K,
  RESOURCE_TYPE_IMAGE_SPEED_4K,
  RESOURCE_TYPE_IMAGE_QUALITY_2K,
  RESOURCE_TYPE_IMAGE_QUALITY_4K,
  RESOURCE_TYPE_IMAGE_LITE,
  RESOURCE_TYPE_SCENARIO_FLASH,
  RESOURCE_TYPE_SCENARIO_FLASH_MID,
  RESOURCE_TYPE_SCENARIO_PRO,
  RESOURCE_TYPE_SCENARIO_ARGON,
  RESOURCE_TYPE_SCENARIO_GPT6_ASTRA,
  RESOURCE_TYPE_SCENARIO_GPT6_SOL,
  RESOURCE_TYPE_SCENARIO_GPT6_LUNA,
  RESOURCE_TYPE_SCENARIO_CLAUDE_SONNET,
  RESOURCE_TYPE_SCENARIO_CLAUDE_OPUS,
  RESOURCE_TYPE_SCENARIO_CLAUDE_FABLE,
} from '../../state/useGenerationState';

interface CreditsData {
  total_available?: number;
  credits_balance: number;
}

interface UseCreditCalculationsParams {
  scenarioGenerationModel: string;
  settings: { image_size?: string };
  /** 動画生成設定の解像度 ('1080' | '720')。image_size に 2K/1K としてマップされ、
   *  image_generation の credit_rate_config 参照に使われる。未指定なら '1080' 相当。 */
  videoResolution?: string;
  creditRatesData: CreditRatesResponse | undefined;
  creditsData: CreditsData | undefined;
  estimatedVideoCredits: number;
  storageData: StorageInfo | undefined;
}

/** 動画解像度 → 画像の image_size ('2K'/'1K') マップ。
 *  backend/services/scenario/generation/frame_generator.py の
 *  `_resolve_frame_billing_params` と同じ規約: 1080→2K / 720→1K /
 *  それ以外 (480p 等) → image_size なし＝基本料率 (1K 相当) で課金・生成される。
 *  従来 FE は 720 以外を一律 2K にしており 480p の表示料率が実課金とずれていた。 */
export function resolveImageSizeFromResolution(resolution: string | undefined): string {
  if (!resolution || resolution === '1080') return '2K'; // 未指定はデフォルト 1080p 相当
  if (resolution === '720') return '1K';
  return '1K'; // 480p ほか: BE では image_size=None → 1K 相当
}

/** model + image_size から credit_rate_config.resource_type を導出する。
 *  backend/services/credit_service._resolve_image_resource_type と同じ規約。 */
export function resolveImageResourceType(model: string, imageSize: string): string | null {
  if (model === GENERATION_MODEL_LITE) {
    // 1K 固定モデル (2K/4K の credit_rate_config 行が存在しない)。imageSize (現在の
    // 動画解像度設定) に関わらず常に単一の resource_type を返す特例。
    return RESOURCE_TYPE_IMAGE_LITE;
  }
  if (model === GENERATION_MODEL_SPEED) {
    if (imageSize === '4K') return RESOURCE_TYPE_IMAGE_SPEED_4K;
    if (imageSize === '2K') return RESOURCE_TYPE_IMAGE_SPEED_2K;
    return RESOURCE_TYPE_IMAGE_SPEED;
  }
  if (model === GENERATION_MODEL_QUALITY) {
    if (imageSize === '4K') return RESOURCE_TYPE_IMAGE_QUALITY_4K;
    // Pro は 1K / 2K / 未指定すべて -2k 料率
    return RESOURCE_TYPE_IMAGE_QUALITY_2K;
  }
  if (model.startsWith('gpt-image-')) {
    // backend と同じ規約: 1K は無接尾、2K/4K は接尾辞付き
    if (imageSize === '4K') return `${model}-4k`;
    if (imageSize === '2K') return `${model}-2k`;
    return model;
  }
  // 既知モデル以外も同じ接尾辞規約で引く
  // (useGenerationCredits.findImageResourceType / backend _resolve_image_resource_type と同期)
  if (imageSize === '4K') return `${model}-4k`;
  if (imageSize === '2K') return `${model}-2k`;
  return model;
}

export function useCreditCalculations({
  scenarioGenerationModel,
  settings,
  videoResolution,
  creditRatesData,
  creditsData,
  estimatedVideoCredits,
  storageData,
}: UseCreditCalculationsParams) {
  // 画像生成のクレジットレートを取得（DBから取得、モデルと解像度に応じて変動）
  const imageCredits = useMemo<number | null>(() => {
    const model = scenarioGenerationModel || DEFAULT_GENERATION_MODEL;
    // シナリオモードでは videoResolution が真の解像度。静止画用の settings.image_size に
    // フォールバックして UI の過渡期を埋める (video 側の解像度セレクタが出る前)。
    const imageSize = videoResolution
      ? resolveImageSizeFromResolution(videoResolution)
      : (settings.image_size || '2K');
    const resourceType = resolveImageResourceType(model, imageSize);
    if (!resourceType) return null;
    if (!creditRatesData?.rates) return null;
    const rate = creditRatesData.rates.find(
      (r) => r.service_type === 'image_generation' && r.resource_type === resourceType
    );
    if (!rate) return null;
    return rate.credits_per_unit;
  }, [scenarioGenerationModel, settings.image_size, videoResolution, creditRatesData]);

  const isCreditInsufficient = useMemo(() => {
    if (!creditsData) return false;
    if (imageCredits === null) return false;
    if (imageCredits <= 0) return false;
    const totalAvailable = creditsData.total_available ?? creditsData.credits_balance;
    return totalAvailable < imageCredits;
  }, [creditsData, imageCredits]);

  const isVideoCreditInsufficient = useMemo(() => {
    if (!creditsData) return false;
    if (estimatedVideoCredits <= 0) return false;
    const totalAvailable = creditsData.total_available ?? creditsData.credits_balance;
    return totalAvailable < estimatedVideoCredits;
  }, [creditsData, estimatedVideoCredits]);

  // ストレージ容量超過チェック
  const isStorageExceeded = useMemo(() => {
    if (!storageData) return false;
    return storageData.is_over_limit;
  }, [storageData]);

  const scenarioModelCredits = useMemo(() => {
    if (!creditRatesData?.rates) {
      return {
        flash: null, flashMid: null, pro: null, argon: null,
        gpt6astra: null, gpt6sol: null, gpt6luna: null,
        claudeSonnet: null, claudeOpus: null, claudeFable: null,
      };
    }
    const flashRate = creditRatesData.rates.find(
      (r) => r.service_type === 'scenario_generation' && r.resource_type === RESOURCE_TYPE_SCENARIO_FLASH
    );
    // Phase 2: 中間枠 (3.5 Flash) のレート。
    const flashMidRate = creditRatesData.rates.find(
      (r) => r.service_type === 'scenario_generation' && r.resource_type === RESOURCE_TYPE_SCENARIO_FLASH_MID
    );
    // Phase 1: pro は新 RESOURCE_TYPE_SCENARIO_PRO (= gemini-3.1-pro-scenario-generation) を参照。
    // 旧 preview 行 (gemini-3.1-pro-preview-scenario-generation) はバックエンド側の
    // LEGACY_MODEL_ALIASES で正規化されるため、ここでは見ない。
    const proRate = creditRatesData.rates.find(
      (r) => r.service_type === 'scenario_generation' && r.resource_type === RESOURCE_TYPE_SCENARIO_PRO
    );
    // 2026-10: Gemini 4 Argon (高品質・3.1 Pro と併存)
    const argonRate = creditRatesData.rates.find(
      (r) => r.service_type === 'scenario_generation' && r.resource_type === RESOURCE_TYPE_SCENARIO_ARGON
    );
    const gpt6AstraRate = creditRatesData.rates.find(
      (r) => r.service_type === 'scenario_generation' && r.resource_type === RESOURCE_TYPE_SCENARIO_GPT6_ASTRA
    );
    const gpt6SolRate = creditRatesData.rates.find(
      (r) => r.service_type === 'scenario_generation' && r.resource_type === RESOURCE_TYPE_SCENARIO_GPT6_SOL
    );
    const gpt6LunaRate = creditRatesData.rates.find(
      (r) => r.service_type === 'scenario_generation' && r.resource_type === RESOURCE_TYPE_SCENARIO_GPT6_LUNA
    );
    const claudeSonnetRate = creditRatesData.rates.find(
      (r) => r.service_type === 'scenario_generation' && r.resource_type === RESOURCE_TYPE_SCENARIO_CLAUDE_SONNET
    );
    const claudeOpusRate = creditRatesData.rates.find(
      (r) => r.service_type === 'scenario_generation' && r.resource_type === RESOURCE_TYPE_SCENARIO_CLAUDE_OPUS
    );
    const claudeFableRate = creditRatesData.rates.find(
      (r) => r.service_type === 'scenario_generation' && r.resource_type === RESOURCE_TYPE_SCENARIO_CLAUDE_FABLE
    );
    return {
      flash: flashRate ? flashRate.credits_per_unit : null,
      flashMid: flashMidRate ? flashMidRate.credits_per_unit : null,
      pro: proRate ? proRate.credits_per_unit : null,
      argon: argonRate ? argonRate.credits_per_unit : null,
      gpt6astra: gpt6AstraRate ? gpt6AstraRate.credits_per_unit : null,
      gpt6sol: gpt6SolRate ? gpt6SolRate.credits_per_unit : null,
      gpt6luna: gpt6LunaRate ? gpt6LunaRate.credits_per_unit : null,
      claudeSonnet: claudeSonnetRate ? claudeSonnetRate.credits_per_unit : null,
      claudeOpus: claudeOpusRate ? claudeOpusRate.credits_per_unit : null,
      claudeFable: claudeFableRate ? claudeFableRate.credits_per_unit : null,
    };
  }, [creditRatesData]);

  const creditBadgeText = useMemo(() => {
    if (imageCredits === null) return '...'; // ロード中でも空にしない
    return imageCredits;
  }, [imageCredits]);

  // 各モデルのクレジット値を取得（imageCreditsと同じimage_sizeベースで算出）
  const modelCredits = useMemo(() => {
    if (!creditRatesData?.rates) {
      return { fast: null, quality: null };
    }
    const imageSize = videoResolution
      ? resolveImageSizeFromResolution(videoResolution)
      : (settings.image_size || '2K');

    const fastResourceType = resolveImageResourceType(GENERATION_MODEL_SPEED, imageSize);
    const fastRate = fastResourceType
      ? creditRatesData.rates.find(
          (r) => r.service_type === 'image_generation' && r.resource_type === fastResourceType
        )
      : undefined;

    const qualityResourceType = resolveImageResourceType(GENERATION_MODEL_QUALITY, imageSize);
    const qualityRate = qualityResourceType
      ? creditRatesData.rates.find(
          (r) => r.service_type === 'image_generation' && r.resource_type === qualityResourceType
        )
      : undefined;

    return {
      fast: fastRate?.credits_per_unit ?? null,
      quality: qualityRate?.credits_per_unit ?? null,
    };
  }, [creditRatesData, settings.image_size, videoResolution]);

  return {
    imageCredits,
    isCreditInsufficient,
    isVideoCreditInsufficient,
    isStorageExceeded,
    scenarioModelCredits,
    creditBadgeText,
    modelCredits,
  };
}
